/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Thin reading-progress indicator that sticks just below the fixed site header
 * (header is `sticky top-0` and 80px tall — see Header.tsx) and fills from left
 * to right as the reader scrolls through the article body. The header auto-hides
 * on scroll-down, so the offset comes from `--header-offset` (published by
 * Header.tsx) rather than a hardcoded 80px — otherwise the bar strands itself
 * mid-viewport once the header is gone.
 *
 * Tracks a target element (the article content section) when given, otherwise
 * falls back to whole-document scroll — which is how <ScrollProgressBar> reuses
 * it for the rest of the site, so every page's bar looks and moves alike.
 * The bar is purely decorative
 * (`aria-hidden`) — scroll position is inherently available to assistive tech —
 * so it never re-renders React on scroll: `useScroll` drives a `MotionValue`
 * that motion writes straight to the DOM transform.
 *
 * SSR-safe: `useScroll`/`useSpring` return 0 during prerender, and the element
 * is only rendered after mount so the prerendered HTML and first client paint
 * agree (at the top of the page progress is 0 → the bar is invisible anyway).
 */

import React, { useEffect, useRef, useState } from 'react';
import { m, useMotionValueEvent, useScroll, useSpring } from 'motion/react';
import { SURFACES, splitLanguageFromPath, trackEvent } from '../lib/gtag';

const READ_MILESTONES = [25, 50, 75, 100] as const;

export const ReadingProgressBar: React.FC<{
  targetRef?: React.RefObject<HTMLElement | null>;
  /** Article slug — when set, `article_read_progress` fires once per milestone
   *  (25/50/75/100%) per pageview, reusing the MotionValue the bar already
   *  computes. Omitted for the generic site-wide bar (no read-depth there). */
  trackSlug?: string;
}> = ({ targetRef, trackSlug }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { scrollYProgress } = useScroll(
    targetRef ? { target: targetRef, offset: ['start start', 'end end'] } : undefined
  );
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  // Milestones the reader actually SCROLLED past. The FIRST measured value
  // per article seeds the baseline (milestones already at/below it never
  // fire) — reading scrollYProgress.get() at mount is useless: useScroll
  // defers its first measurement to the next animation frame, so a mount-time
  // read always sees 0 and a short article (progress clamped to 1) would fire
  // all four milestones on frame one. The state resets when trackSlug changes
  // (the component stays mounted across article→article navigation), so the
  // previous article's final progress never suppresses the next one's.
  const milestones = useRef<{ seeded: boolean; fired: Set<number> }>({
    seeded: false,
    fired: new Set(),
  });
  useEffect(() => {
    milestones.current = { seeded: false, fired: new Set() };
  }, [trackSlug]);

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    if (!trackSlug) return;
    const pct = v * 100;
    const st = milestones.current;
    if (!st.seeded) {
      // Baseline: whatever is already in view at first measurement (short
      // article, restored scroll position) is not deliberate reading.
      st.seeded = true;
      for (const m of READ_MILESTONES) if (pct >= m) st.fired.add(m);
      return;
    }
    for (const m of READ_MILESTONES) {
      if (pct >= m && !st.fired.has(m)) {
        st.fired.add(m);
        trackEvent('article_read_progress', {
          article_slug: trackSlug,
          percent: m,
          surface: SURFACES.article,
          language: splitLanguageFromPath(window.location.pathname).language,
        });
      }
    }
  });

  // Render nothing until mounted so the prerendered HTML and the first client
  // render match (avoids hydrating a motion transform that only exists client-side).
  if (!mounted) return null;

  return (
    <div
      aria-hidden="true"
      // Pinned at the header's full height and translated up by what the
      // header has given up (--header-offset): a compositor-only transform
      // instead of an animated `top`, which re-laid out the page on every
      // frame of the header's slide.
      className="fixed inset-x-0 top-20 z-40 h-1 bg-slate-800/40 [html.light_&]:bg-slate-700/60 translate-y-[calc(var(--header-offset)_-_5rem)] transition-transform duration-300 motion-reduce:transition-none"
    >
      <m.div
        style={{ scaleX }}
        className="h-full origin-left bg-gradient-to-r from-emerald-500 to-emerald-400 [html.light_&]:from-emerald-600 [html.light_&]:to-emerald-500"
      />
    </div>
  );
};
